import { useEffect, useState } from 'react';
import Gallery from './components/Gallery';
import PhotoGalleryPage from './components/PhotoGalleryPage';
import RoomByRoom from './components/RoomByRoom';
import Footer from './components/Footer';
import SectionHead from './components/SectionHead';
import { trackAirbnbClick } from './analytics';
import LocalGuides from './components/LocalGuides';
import GuidePage from './components/GuidePage';
import GuidesPage from './components/GuidesPage';
import { guides } from './guides';

const ADDRESS = '4661 Hotel Lake Road, Garden Bay, BC V0N 1S1';
const AIRBNB_URL =
  'https://www.airbnb.ca/rooms/21152569?unique_share_id=4823a01f-3fa7-4fec-a058-bfc08b0d69ec&viralityEntryPoint=1&s=76';

const socials = {
  facebook: 'https://www.facebook.com/turtleislandcottage/',
  instagram:
    'https://www.instagram.com/explore/locations/198921993861699/turtle-island-cottage/'
};

const highlights = [
  {
    title: 'Private beach & dock',
    body: 'Step off the deck onto your own beach. Swim, kayak, or just sit on the dock.'
  },
  {
    title: 'Room to unwind',
    body: 'A cozy layout that works for families or a quiet getaway.'
  },
  {
    title: 'Sunshine Coast calm',
    body: 'Garden Bay energy. Slow down and breathe.'
  }
];

function BedIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 13V9a2 2 0 012-2h14a2 2 0 012 2v4" />
      <rect x="2" y="13" width="18" height="5" rx="1.5" />
      <path d="M6 11h4" />
      <path d="M12 11h4" />
    </svg>
  );
}

function WavesIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 11c1.5-2 3-2 4.5 0s3 2 4.5 0 3-2 4.5 0 3 2 4.5 0" />
      <path d="M2 15c1.5-2 3-2 4.5 0s3 2 4.5 0 3-2 4.5 0" />
    </svg>
  );
}

function KitchenIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M7 3v6M4 3v6M10 3v6" />
      <path d="M4 9a3 3 0 006 0" />
      <path d="M16 3v4a2 2 0 002 2v10" />
    </svg>
  );
}

function LaundryIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="3" y="3" width="16" height="16" rx="2" />
      <circle cx="11" cy="12" r="4" />
      <path d="M5 5h3" />
    </svg>
  );
}

function ParkingIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 10l2.5-5h9L18 10" />
      <rect x="3" y="10" width="16" height="7" rx="1.5" />
      <circle cx="7" cy="17" r="1.5" />
      <circle cx="15" cy="17" r="1.5" />
    </svg>
  );
}

function SeatingIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M6 9V5a2 2 0 014 0v4" />
      <path d="M12 9V5a2 2 0 014 0v4" />
      <path d="M4 9h14" />
      <path d="M6 9v9" />
      <path d="M16 9v9" />
      <path d="M6 14h10" />
    </svg>
  );
}

function DockIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 18h16" />
      <path d="M11 6v12" />
      <path d="M6 18v-5" />
      <path d="M16 18v-5" />
      <path d="M3 13h16" />
    </svg>
  );
}

function FamilyIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="7.5" cy="5.5" r="2.5" />
      <circle cx="14.5" cy="5.5" r="2.5" />
      <path d="M3 19v-2a4.5 4.5 0 014.5-4.5h7a4.5 4.5 0 014.5 4.5v2" />
    </svg>
  );
}

const amenities = [
  { label: '3 bedrooms', icon: <BedIcon /> },
  { label: 'Private beach & lake access', icon: <WavesIcon /> },
  { label: 'Private dock', icon: <DockIcon /> },
  { label: 'Full kitchen', icon: <KitchenIcon /> },
  { label: 'Laundry', icon: <LaundryIcon /> },
  { label: 'Parking', icon: <ParkingIcon /> },
  { label: 'Outdoor seating', icon: <SeatingIcon /> },
  { label: 'Good for families', icon: <FamilyIcon /> }
];

const navLinks = [
  { href: '#photos', label: 'Photos' },
  { href: '#rooms', label: 'Rooms' },
  { href: '#amenities', label: 'Amenities' },
  { href: '#off-season', label: 'Fall & Spring' },
  { href: '#location', label: 'Location' },
  { href: '#guides', label: 'Local guides' },
  { href: '#contact', label: 'Contact' }
];

export default function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Cross-page fragment links can arrive before React mounts their target.
    const scrollToSection = () => {
      const id = window.location.hash.slice(1);
      if (id) document.getElementById(id)?.scrollIntoView();
    };

    scrollToSection();
    window.addEventListener('hashchange', scrollToSection);
    return () => window.removeEventListener('hashchange', scrollToSection);
  }, []);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path !== '/') {
    const guide = guides.find((item) => item.href.replace(/\/$/, '') === path);
    return (
      <>
        {path === '/photos' ? (
          <PhotoGalleryPage />
        ) : path === '/guides' ? (
          <GuidesPage />
        ) : (
          <GuidePage guide={guide} />
        )}
        <Footer socials={socials} />
      </>
    );
  }

  return (
    <div className="min-h-screen">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-forest focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-50 border-b border-forest/10 bg-birch/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-baseline gap-2">
            <span className="font-display italic font-semibold text-xl text-forest">
              Turtle Island Cottage
            </span>
            <span className="hidden text-sm text-bark/55 sm:inline">
              Garden Bay, BC
            </span>
          </div>

          <nav className="hidden items-center gap-6 text-sm lg:flex">
            {navLinks.map((link) => (
              <a
                key={link.href}
                className="rounded-md px-2 py-1 text-bark/70 transition hover:text-forest focus:outline-hidden focus:ring-2 focus:ring-forest focus:ring-offset-2"
                href={link.href}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="rounded-lg p-2 text-forest transition hover:bg-forest/10 focus:outline-hidden focus:ring-2 focus:ring-forest lg:hidden"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>

        {mobileMenuOpen && (
          <nav className="border-t border-forest/10 bg-birch px-4 py-4 lg:hidden">
            <ul className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={closeMobileMenu}
                    className="block rounded-lg px-4 py-3 text-base font-medium text-bark transition hover:bg-forest/10 hover:text-forest focus:outline-hidden focus:ring-2 focus:ring-forest active:bg-forest/15"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>

      <main id="main-content">
        {/* Hero — editorial gradient, text anchored to bottom */}
        <section className="relative flex min-h-[80vh] items-end">
          <div className="absolute inset-0">
            <img
              src="/images/hero/hero.jpg"
              alt="Turtle Island Cottage lakefront view on Hotel Lake in Garden Bay, BC"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-linear-to-t/srgb from-forest via-forest/55 to-transparent" />
          </div>

          <div className="relative mx-auto w-full max-w-6xl px-4 py-16 md:py-24">
            <div className="max-w-3xl">
              <h1 className="font-display italic font-semibold text-5xl leading-[1.05] tracking-tight text-white sm:text-6xl md:text-7xl">
                A lakefront cottage escape on BC's Sunshine Coast.
              </h1>
              <p className="mt-5 text-lg text-white/85">
                Turtle Island Cottage sits by Hotel Lake in Garden Bay. Bring a
                book. Bring your people. Let the week feel longer.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#photos"
                  className="rounded-xl bg-white px-5 py-3 text-sm font-semibold text-forest transition hover:bg-glacial-light focus:outline-hidden focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-forest active:bg-glacial"
                >
                  View photos
                </a>
                <a
                  href={AIRBNB_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackAirbnbClick('hero')}
                  aria-label="Check availability and book Turtle Island Cottage on Airbnb"
                  className="rounded-xl border border-white/40 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/20 focus:outline-hidden focus:ring-2 focus:ring-white active:bg-white/25"
                >
                  Check availability on Airbnb
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Highlights — forest green bridges from hero gradient */}
        <section className="bg-forest">
          <div className="mx-auto max-w-6xl px-4 py-14">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {highlights.map((h) => (
                <div
                  key={h.title}
                  className="rounded-2xl border border-white/10 bg-forest-hover p-6"
                >
                  <div className="mb-3 h-0.5 w-8 bg-cedar" />
                  <h3 className="font-semibold text-white">{h.title}</h3>
                  <p className="mt-2 text-sm text-white/75">{h.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id="photos"
          className="mx-auto max-w-6xl px-4 pb-14 pt-14 scroll-mt-20"
        >
          <SectionHead title="Photos" />
          <Gallery />
        </section>

        <RoomByRoom />

        <section
          id="amenities"
          className="scroll-mt-20"
          style={{ backgroundColor: 'rgba(197,221,214,0.3)' }}
        >
          <div className="mx-auto max-w-6xl px-4 py-14">
            <SectionHead
              title="Amenities"
              subtitle="Everything you need for a comfortable stay."
            />
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {amenities.map((a) => (
                <li
                  key={a.label}
                  className="flex items-center gap-3 rounded-xl border border-forest/10 bg-white p-4 text-sm font-medium text-forest"
                >
                  <span className="flex-none text-cedar">{a.icon}</span>
                  {a.label}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="off-season" className="bg-birch scroll-mt-20">
          <div className="mx-auto max-w-6xl px-4 py-14">
            <SectionHead
              title="The quieter side of the Sunshine Coast"
              subtitle="Fall and spring trade summer crowds for slower mornings, peaceful lake views, and more time to settle in."
            />
            <div className="grid gap-5 md:grid-cols-3">
              {[
                [
                  'A weekend that feels longer',
                  'Make the cottage your base for a few unhurried days in Garden Bay, with the lake just outside.'
                ],
                [
                  'Bring your favourite people',
                  'Three bedrooms give families and small groups room to reconnect without rushing anywhere.'
                ],
                [
                  'Stay close to the water',
                  'Coffee by the lake, a walk along the shore, and quiet evenings make cooler months worth the trip.'
                ]
              ].map(([title, body]) => (
                <article
                  key={title}
                  className="rounded-2xl border border-forest/10 bg-white p-6"
                >
                  <h3 className="font-semibold text-forest">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-bark/70">{body}</p>
                </article>
              ))}
            </div>
            <div className="mt-8">
              <a
                href={AIRBNB_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackAirbnbClick('shoulder-season')}
                className="inline-flex rounded-xl bg-forest px-5 py-3 text-sm font-semibold text-white transition hover:bg-forest-hover focus:outline-hidden focus:ring-2 focus:ring-forest focus:ring-offset-2"
              >
                See dates and pricing on Airbnb
              </a>
              <p className="mt-3 text-xs text-bark/55">
                Availability, pricing, reservations, and payments are handled
                securely on Airbnb.
              </p>
            </div>
          </div>
        </section>

        <section
          id="location"
          className="mx-auto max-w-6xl px-4 py-14 scroll-mt-20"
        >
          <SectionHead title="Location" subtitle={ADDRESS} />

          <div className="grid gap-6 sm:grid-cols-2">
            <div className="rounded-2xl border border-forest/15 bg-white p-6">
              <h3 className="font-semibold text-forest">Get directions</h3>
              <p className="mt-2 text-sm text-bark/65">
                Open the address in Google Maps.
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  className="rounded-xl bg-forest px-4 py-2 text-sm font-medium text-white transition hover:bg-forest-hover focus:outline-hidden focus:ring-2 focus:ring-forest focus:ring-offset-2 active:bg-forest-dark"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    ADDRESS
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in Google Maps
                </a>
                <a
                  className="rounded-xl border border-forest/20 px-4 py-2 text-sm font-medium text-forest transition hover:bg-glacial-light focus:outline-hidden focus:ring-2 focus:ring-forest focus:ring-offset-2 active:bg-glacial-light"
                  href={socials.facebook}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Ask a question on Facebook"
                >
                  Ask a question
                </a>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-forest/15 bg-white">
              <iframe
                title="Map showing Turtle Island Cottage location"
                className="h-80 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${encodeURIComponent(
                  ADDRESS
                )}&output=embed`}
              />
            </div>
          </div>
        </section>

        <LocalGuides limit={3} />

        <section id="contact" className="bg-forest text-white scroll-mt-20">
          <div className="mx-auto max-w-6xl px-4 py-14">
            <div className="grid gap-8 sm:grid-cols-2">
              <div>
                <h2 className="font-display italic font-semibold text-3xl leading-tight text-white">
                  Ready for a few days at the lake?
                </h2>
                <div className="mt-2 h-0.5 w-10 bg-cedar" />
                <p className="mt-4 text-white/80">
                  Check available dates and complete your reservation on Airbnb.
                </p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href={AIRBNB_URL}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackAirbnbClick('footer')}
                    aria-label="Book Turtle Island Cottage on Airbnb"
                    className="rounded-xl bg-cedar px-5 py-3 text-sm font-semibold text-bark transition hover:opacity-90 focus:outline-hidden focus:ring-2 focus:ring-cedar focus:ring-offset-2 focus:ring-offset-forest active:opacity-80"
                  >
                    Book on Airbnb
                  </a>
                  <a
                    href={socials.facebook}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="View us on Facebook"
                    className="rounded-xl border border-white/30 bg-white/10 px-5 py-3 text-sm font-semibold transition hover:bg-white/20 focus:outline-hidden focus:ring-2 focus:ring-white active:bg-white/25"
                  >
                    Facebook
                  </a>
                  <a
                    href={socials.instagram}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="View us on Instagram"
                    className="rounded-xl border border-white/30 bg-white/10 px-5 py-3 text-sm font-semibold transition hover:bg-white/20 focus:outline-hidden focus:ring-2 focus:ring-white active:bg-white/25"
                  >
                    Instagram
                  </a>
                </div>
              </div>

              <div className="rounded-2xl border border-white/15 bg-white/10 p-6">
                <h3 className="font-semibold text-white">Tag us online</h3>
                <p className="mt-3 text-sm text-white/80">
                  Share your stay on Instagram with #turtleislandcottage and
                  #hotellake, and tag these locations:
                </p>
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-3 text-sm">
                  <a
                    href={socials.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-xs underline underline-offset-4 transition hover:text-cedar focus:outline-hidden focus:ring-2 focus:ring-white"
                  >
                    Turtle Island Cottage
                  </a>
                  <a
                    href="https://www.instagram.com/explore/locations/177334598956002/hotel-lake/"
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-xs underline underline-offset-4 transition hover:text-cedar focus:outline-hidden focus:ring-2 focus:ring-white"
                  >
                    Hotel Lake
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer socials={socials} />
    </div>
  );
}
